"use client";

import { type RefObject } from "react";
import { MEDIA_QUERY } from "~/constants/breakpoints";
import { useIsomorphicLayoutEffect } from "~/hooks/useIsomorphicLayoutEffect";
import {
    HERO_PARALLAX_DRIFT_RATIO,
    syncHeroCoverMedia,
} from "./parallax";

const DESKTOP_MEDIA_QUERY = "(min-width: 1024px)";
const COPY_SCROLL_TRAVEL_RATIO = 0.18;
const COPY_FADE_START_RATIO = 0.15;
const COPY_FADE_END_RATIO = 0.65;
const INITIAL_ZOOM_TRANSFORM = "scale(1)";
const INITIAL_ZOOM_TRANSITION =
    "transform 1800ms cubic-bezier(.22, 1, .36, 1)";

const clamp = (value: number, minimum: number, maximum: number) => (
    Math.min(Math.max(value, minimum), maximum)
);

type UseHeroParallaxArgs = {
    clipRef: RefObject<HTMLElement | null>;
    layerRef: RefObject<HTMLElement | null>;
    mediaRef: RefObject<HTMLElement | null>;
    zoomRef: RefObject<HTMLElement | null>;
};

export function useHeroParallax({
    clipRef,
    layerRef,
    mediaRef,
    zoomRef,
}: UseHeroParallaxArgs) {
    useIsomorphicLayoutEffect(() => {
        // Read here, not in render, so the markup never depends on a client-only value.
        if (window.matchMedia(MEDIA_QUERY.REDUCED_MOTION).matches) {
            return;
        }

        const clip = clipRef.current;
        const layer = layerRef.current;
        const media = mediaRef.current;
        const zoom = zoomRef.current;

        if (!clip || !layer || !media || !zoom) {
            return;
        }

        const desktopMedia = window.matchMedia(DESKTOP_MEDIA_QUERY);
        let cleanupAnimation: (() => void) | undefined;

        const initializeParallax = () => {
            const images = Array.from(media.querySelectorAll("img"));
            const hero = clip.closest<HTMLElement>("[data-hero-root]");
            const copy = hero?.querySelector<HTMLElement>("[data-hero-copy]");
            let heroTop = 0;
            let heroHeight = 1;
            let scrollFrame: number | null = null;
            let zoomFrame: number | null = null;
            const syncMedia = () => {
                syncHeroCoverMedia(media);
            };
            const measure = () => {
                heroTop = clip.getBoundingClientRect().top + window.scrollY;
                heroHeight = Math.max(clip.offsetHeight, 1);
            };
            const render = () => {
                scrollFrame = null;
                const scrollDistance = clamp(
                    window.scrollY - heroTop,
                    0,
                    heroHeight
                );
                const progress = scrollDistance / heroHeight;

                layer.style.transform = `translate3d(0, ${
                    (-scrollDistance * HERO_PARALLAX_DRIFT_RATIO).toFixed(2)
                }px, 0)`;

                if (copy) {
                    copy.style.transform = `translate3d(0, ${
                        (-scrollDistance * COPY_SCROLL_TRAVEL_RATIO).toFixed(2)
                    }px, 0)`;
                    copy.style.opacity = (
                        1 - clamp(
                            (progress - COPY_FADE_START_RATIO)
                            / (COPY_FADE_END_RATIO - COPY_FADE_START_RATIO),
                            0,
                            1
                        )
                    ).toFixed(3);
                }
            };
            const scheduleRender = () => {
                if (scrollFrame == null) {
                    scrollFrame = requestAnimationFrame(render);
                }
            };
            const playInitialZoom = () => {
                if (zoomFrame != null) {
                    cancelAnimationFrame(zoomFrame);
                }
                zoom.style.transition = INITIAL_ZOOM_TRANSITION;
                zoomFrame = requestAnimationFrame(() => {
                    zoomFrame = null;
                    if (desktopMedia.matches) {
                        zoom.style.transform = INITIAL_ZOOM_TRANSFORM;
                    }
                });
            };

            measure();
            syncMedia();
            images.forEach((image) => {
                image.addEventListener("load", syncMedia);
            });
            window.addEventListener("scroll", scheduleRender, {
                passive: true,
            });

            const resizeObserver = new ResizeObserver(() => {
                measure();
                syncMedia();
                scheduleRender();
            });
            resizeObserver.observe(clip);

            const heroImage = images[0];
            if (heroImage?.complete) {
                playInitialZoom();
            } else {
                heroImage?.addEventListener("load", playInitialZoom, {
                    once: true,
                });
            }
            render();

            cleanupAnimation = () => {
                images.forEach((image) => {
                    image.removeEventListener("load", syncMedia);
                });
                heroImage?.removeEventListener("load", playInitialZoom);
                window.removeEventListener("scroll", scheduleRender);
                resizeObserver.disconnect();
                if (scrollFrame != null) {
                    cancelAnimationFrame(scrollFrame);
                }
                if (zoomFrame != null) {
                    cancelAnimationFrame(zoomFrame);
                }
                media.style.removeProperty("aspect-ratio");
                layer.style.removeProperty("transform");
                zoom.style.removeProperty("transition");
                zoom.style.removeProperty("transform");
                copy?.style.removeProperty("transform");
                copy?.style.removeProperty("opacity");
            };
        };

        const activateParallax = () => {
            initializeParallax();
        };
        const handleDesktopChange = () => {
            cleanupAnimation?.();
            cleanupAnimation = undefined;

            if (desktopMedia.matches) {
                activateParallax();
            }
        };

        desktopMedia.addEventListener("change", handleDesktopChange);
        if (desktopMedia.matches) {
            activateParallax();
        }

        return () => {
            desktopMedia.removeEventListener("change", handleDesktopChange);
            cleanupAnimation?.();
        };
    }, [clipRef, layerRef, mediaRef, zoomRef]);
}
